import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import Spinner from "../../../../reusableComponents/spinner/spinner";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/hooks";

import {
  getTrainingSchools,
  getTrainingDomains,
} from "../../../../../store/thunks/portalManagementThunk";

import { addTrainingUser } from "../../../../../store/thunks/systemManagementThunk";

import {
  setSelectedSchoolDealer,
} from "../../../../../store/features/portalManagement/portalManagementSlice";

import type { TrainingUserInfo } from "../userManagementModel";

import { RoleIndex } from "../../../../../models/role";
import { UserType } from "../../../../../models/userType";

interface CreateUserDialogProps {
  open: boolean;
  handleCreateClose: (message?: string) => void;
}

type ErrorState = Record<
  "user_role" | "user_school" | "user_domain" | "user_admin" | "global",
  { hasError: boolean; message: string }
>;

const CreateUserDialog = ({ open, handleCreateClose }: CreateUserDialogProps) => {

  const [showSchools, setShowSchools] = useState(false);
  const [showDomains, setShowDomains] = useState(false);

  const dispatch = useAppDispatch();

  const {
    addTrainingUserLoading
  } = useAppSelector((s: any) => s.systemManagement);

  const {
    clients,
    trainingRoles,
    trainingSchools,
    trainingDomains,
  } = useAppSelector((s: any) => s.portalManagement);

  const base_error = {
    global: { hasError: false, message: ""},
    user_role: { hasError: false, message: "" },
    user_domain: { hasError: false, message: "" },
    user_school: { hasError: false, message: "" },
    user_admin: { hasError: false, message: "" }
  }

  const [errors, setErrors] = useState<ErrorState>(base_error);

  const clearErrors = () =>
    setErrors(base_error);

  const setError = (field: keyof ErrorState, message: string) =>
    setErrors((prev) => ({ ...prev, [field]: { hasError: !!message, message } }));

  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Required"),
    first_name: Yup.string().min(1).max(50).required("Required"),
    last_name: Yup.string().min(1).max(50).required("Required"),
    user_role: Yup.mixed().required("Required"),
    user_school: Yup.object().nullable().when("user_role", {
      is: (val: any) => val?.role_type === UserType.Name.LWS_SCHOOL,
      then: (schema) => schema
        .nonNullable("School is required")
        .required("School is required")
        .test(
          "valid-school",
          "School must be selected from the list",
          (value: any) => !!value && (trainingSchools || []).some((s: any) => s.name === value.name)
        ),
      otherwise: (schema) => schema.optional(),
    }),
    // user_school: Yup.object().nullable().when("user_domain", {
    //   is: (val: any) => !val,
    //   then: (schema) => schema.required("School is required if no domain selected"),
    //   otherwise: (schema) => schema.optional(),
    // }),
    // user_school: Yup.object().nullable().optional(),
    user_domain: Yup.string().nullable().min(1).max(50).when("user_role", {
      is: (val: any) => val?.role_type === UserType.Name.LWS_DEALER,
      then: (schema) => schema
        .nonNullable("Domain is required")
        .required("Domain is required")
        .test(
          "valid-domain",
          "Domain must be selected from the list",
          (value: any) => {
            if (!value) return false;
            const domains = Array.from(new Set((trainingDomains || []).map((e: any) => e.email.split('@')[1]?.split('.')[0])));
            return domains.includes(value);
          }
        ),
      otherwise: (schema) => schema.optional(),
    }),
    user_admin: Yup.boolean().nullable().optional()
  });

  const createUserForm = useFormik<TrainingUserInfo>({
    initialValues: {
      email: "",
      first_name: "",
      last_name: "",
      role_index: RoleIndex.ClientUser,
      user_role: null,
      user_domain: null,
      user_school: null,
      user_admin: null
    },

    validationSchema,
    onSubmit: (values, { resetForm }) => {
      clearErrors();

      const payload: any = {
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        role_index: RoleIndex.ClientUser,
        client_user_type_index: UserType.Index[values.user_role?.role_type as keyof typeof UserType.Index],
        client: clients.find((c: any) => c.customer_id === 1001)?.client_index,
        role_type: values.user_role.role_type,
        user_domain: UserType.Name.LWS_DEALER === values.user_role.role_type ? values.user_domain : null,
        user_school: UserType.Name.LWS_SCHOOL === values.user_role.role_type ? values.user_school.name : null,
        user_admin: values?.user_admin
      };
      
      dispatch(addTrainingUser({ createUserPayload: payload })).then((result: any) => {
        if (result.payload?.result === true) {
          handleCreateClose(result.payload.message);
          resetForm();
        } else {
          console.log(result.payload.message);
          setError("global", "Failed to create user.");
        }
      });
    },
  });

  useEffect(() => {
    if (!open) return;
    clearErrors();
    createUserForm.resetForm();
    setShowSchools(false);
    setShowDomains(false);
  }, [open]);

  // ────────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────────

  const handleRoleChange = (_: SyntheticEvent, role: any) => {
    clearErrors();

    ["user_school", "user_domain", "user_admin"].forEach(field => {
      createUserForm.setFieldError(field, undefined);
      createUserForm.setFieldValue(field, null);
    });

    if(!role) return;

    createUserForm.setFieldValue("user_role", role);

    if(role.role_type === UserType.Name.LWS_SCHOOL) {
      dispatch(getTrainingSchools({}))
      setShowSchools(true);
      setShowDomains(false);
    } 
    if(role.role_type === UserType.Name.LWS_DEALER) {
      dispatch(getTrainingDomains({}))
      setShowDomains(true);
      setShowSchools(false);
    }

    // LWS_SUPPORT gets the same all-records scope as LWS_ADMIN (user_admin
    // drives the backend's 1=1 people_user scope); only user management differs.
    if(role.role_type === UserType.Name.LWS_ADMIN || role.role_type === UserType.Name.LWS_SUPPORT) {
      createUserForm.setFieldValue("user_admin", true);
      setShowDomains(false);
      setShowSchools(false);
    }
  };

  const handleSchoolChange = (_: SyntheticEvent, school: any) => {
    clearErrors();
    if(!school) return;
    createUserForm.setFieldValue("user_school", school, false);
    createUserForm.setFieldTouched("user_school", false, false);
    createUserForm.setFieldError("user_school", undefined);
    setShowDomains(false);
    ["user_domain", "user_admin"].forEach(f => {
      createUserForm.setFieldValue(f, null, false);
      createUserForm.setFieldTouched(f, false, false);
      createUserForm.setFieldError(f, undefined);
    });
  };

  const handleDomainChange = (_: SyntheticEvent, domain: any) => {
    clearErrors();
    if(!domain) return;
    createUserForm.setFieldValue("user_domain", domain, false);
    createUserForm.setFieldTouched("user_domain", false, false);
    createUserForm.setFieldError("user_domain", undefined);
    setShowSchools(false);
    ["user_school", "user_admin"].forEach(f => {
      createUserForm.setFieldValue(f, null, false);
      createUserForm.setFieldTouched(f, false, false);
      createUserForm.setFieldError(f, undefined);
    });
  };

  const handleDialogClose = (_?: any, reason?: string) => {
    clearErrors()
    setSelectedSchoolDealer(null);
    createUserForm.resetForm();
    if (!reason || reason !== "backdropClick") {
      handleCreateClose();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={!!open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ bgcolor: "#3B934A", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" fontWeight="bold" component="span">
          Add User
        </Typography>
        <IconButton onClick={handleDialogClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Spinner loading={addTrainingUserLoading} />

      <form onSubmit={createUserForm.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ p: 2 }}>
            {/* Email, First Name, Last Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={createUserForm.values.email}
                onChange={createUserForm.handleChange}
                error={createUserForm.touched.email && !!createUserForm.errors.email}
                helperText={createUserForm.touched.email && typeof createUserForm.errors.email === "string" ? createUserForm.errors.email : ""}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={createUserForm.values.first_name}
                onChange={createUserForm.handleChange}
                error={createUserForm.touched.first_name && !!createUserForm.errors.first_name}
                helperText={createUserForm.touched.first_name && typeof createUserForm.errors.first_name === "string" ? createUserForm.errors.first_name : ""}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={createUserForm.values.last_name}
                onChange={createUserForm.handleChange}
                error={createUserForm.touched.last_name && !!createUserForm.errors.last_name}
                helperText={createUserForm.touched.last_name && typeof createUserForm.errors.last_name === "string" ? createUserForm.errors.last_name : ""}
              />
            </Grid>

            {/* Role */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={trainingRoles || []}
                getOptionLabel={(opt) => opt?.role_type}
                value={createUserForm.values.user_role}
                onChange={handleRoleChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Role"
                    error={errors.user_role.hasError || (createUserForm.touched.user_role && !!createUserForm.errors.user_role)}
                    helperText={errors.user_role.message || (createUserForm.touched.user_role && typeof createUserForm.errors.user_role === "string" ? createUserForm.errors.user_role : "")}
                  />
                )}
              />
            </Grid>
            
            {showSchools && (<Grid item xs={12} sm={6}>
              <Autocomplete
                options={trainingSchools || []}
                getOptionLabel={(opt) => opt?.name}
                value={createUserForm.values.user_school}
                onChange={handleSchoolChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="School(s)"
                    error={errors.user_school.hasError || (createUserForm.touched.user_school && !!createUserForm.errors.user_school)}
                    helperText={errors.user_school.message || (createUserForm.touched.user_school && typeof createUserForm.errors.user_school === "string" ? createUserForm.errors.user_school : "")}
                  />
                )}
              />
            </Grid>)}

            {showDomains && (<Grid item xs={12} sm={6}>
              <Autocomplete
                options={Array.from(new Set((trainingDomains || []).map((e: any) => e.email.split('@')[1]?.split('.')[0])))}
                value={createUserForm.values.user_domain}
                onChange={handleDomainChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Domain(s)"
                    error={errors.user_domain.hasError || (createUserForm.touched.user_domain && !!createUserForm.errors.user_domain)}
                    helperText={errors.user_domain.message || (createUserForm.touched.user_domain && typeof createUserForm.errors.user_domain === "string" ? createUserForm.errors.user_domain : "")}
                  />
                )}
              />
            </Grid>)}

            {errors.global.hasError && (
              <Grid item xs={12}>
                <Typography color="error" align="center">
                  {errors.global.message}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #ddd", p: 2 }}>
          <Button variant="outlined" color="error" onClick={handleDialogClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={createUserForm.isSubmitting || addTrainingUserLoading}>
            Create User
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateUserDialog;
